<template>
    <div class="col-12 row px-3 py-2">
        <div class="col-12 col-md-4 g-0">
            <h6 class="mb-2 d-flex align-items-center">
                <b class="me-2">Pre-Screening</b>
            </h6>
            <div class="form-group col-12">
                <label for="tb-test"> TB Test </label>
                <div class="form-check custom-radio form-check-inline">
                    <input
                        type="radio"
                        id="tbTestTrue"
                        name="hasTBTest"
                        class="form-check-input"
                        v-model="patientHealthHistory.pediatricHistory.tbTest"
                        :value="true"
                    />
                    <label class="form-check-label" for="tbTestTrue"> Yes </label>
                </div>
                <div class="form-check custom-radio form-check-inline">
                    <input
                        type="radio"
                        id="tbTestFalse"
                        name="hasTBTest"
                        class="form-check-input"
                        v-model="patientHealthHistory.pediatricHistory.tbTest"
                        :value="false"
                    />
                    <label class="form-check-label" for="tbTestFalse"> No </label>
                </div>
            </div>
            <div class="form-group col-12">
                <label for="lead-screening"> Lead Screening </label>
                <div class="form-check custom-radio form-check-inline">
                    <input
                        type="radio"
                        id="leadScreeningTrue"
                        name="hasLeadScreening"
                        class="form-check-input"
                        v-model="patientHealthHistory.pediatricHistory.leadScreening"
                        :value="true"
                    />
                    <label class="form-check-label" for="leadScreeningTrue"> Yes </label>
                </div>
                <div class="form-check custom-radio form-check-inline">
                    <input
                        type="radio"
                        id="leadScreeningFalse"
                        name="hasLeadScreening"
                        class="form-check-input"
                        v-model="patientHealthHistory.pediatricHistory.leadScreening"
                        :value="false"
                    />
                    <label class="form-check-label" for="leadScreeningFalse"> No </label>
                </div>
            </div>
            <div class="form-group w-90 col-12">
                <label for="pediatric-other"> Other </label>
                <input
                    type="text"
                    class="form-control"
                    v-model="patientHealthHistory.pediatricHistory.otherPediatricScreening"
                    id="pediatric-other"
                />
            </div>
        </div>
        <div class="col-md-8 g-0">
            <h6 class="mb-2 d-flex align-items-center">
                <b class="me-2">Newborn History</b>
            </h6>
            <div class="col-12 row">
                <div class="col-md-6">
                    <div class="form-check custom-checkbox form-check-inline me-3">
                        <input
                            type="checkbox"
                            id="newborn-history-na"
                            class="form-check-input"
                            v-model="patientHealthHistory.pediatricHistory.newBornHistory"
                        />
                        <label class="form-check-label" for="newborn-history-na">
                            <span class="me-1">N/A</span>
                        </label>
                    </div>

                    <span v-if="!patientHealthHistory.pediatricHistory.newBornHistory">
                        <div class="form-group">
                            <label for="gestational-age"> Gestational Age at Birth </label>
                            <input
                                type="text"
                                class="form-control"
                                v-model="patientHealthHistory.pediatricHistory.gestationalAgeAtBirth"
                                id="gestational-age"
                                placeholder="Weeks"
                            />
                        </div>
                        <div class="ps-0">
                            <strong class="py-1">Length</strong>
                            <div class="d-flex row align-items-end">
                                <div class="col-md-6">
                                    <label for="lengthScale" class="mb-0 mt-1">
                                        <strong>Scale</strong>
                                    </label>
                                    <EnumTypeaheadInput
                                        :enum-list="lengthScaleEnum"
                                        v-model:prefill-value="patientHealthHistory.pediatricHistory.lengthScale"
                                        placeholder="Select Scale"
                                    />
                                </div>
                                <div class="col-md-6">
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="patientHealthHistory.pediatricHistory.lengthAtBirth"
                                        :placeholder="`${isLengthImperial ? 'in.' : 'cm.'}`"
                                        maxlength="5"
                                        number-only="decimal"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="fontanels"> Fontanels </label>
                            <div class="d-flex">
                                <div
                                    class="form-check custom-checkbox form-check-inline me-3"
                                    v-for="(value, key) of fontanelsEnum"
                                >
                                    <input
                                        type="checkbox"
                                        :id="`fontanel-${value.name}`"
                                        class="form-check-input"
                                        v-model="patientHealthHistory.pediatricHistory.fontanels"
                                        :value="value.value"
                                    />
                                    <label class="form-check-label" :for="`fontanel-${value.name}`">
                                        <span class="me-1">{{ value.name }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="umbilicus"> Umbilicus </label>
                            <div class="row ps-3 col-12">
                                <div
                                    class="form-check custom-checkbox form-check-inline col-md-5"
                                    v-for="(value, key) of umbilicusEnum"
                                >
                                    <input
                                        type="checkbox"
                                        :id="`umbilicus-${value.name}`"
                                        class="form-check-input"
                                        v-model="patientHealthHistory.pediatricHistory.umbilicus"
                                        :value="value.value"
                                    />
                                    <label class="form-check-label" :for="`umbilicus-${value.name}`">
                                        <span class="me-1">{{ value.name }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </span>
                </div>
                <div class="col-md-6" v-if="!patientHealthHistory.pediatricHistory.newBornHistory">
                    <div class="form-group">
                        <label for="newborn-screening-results"> Newborn Screening Results </label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="patientHealthHistory.pediatricHistory.newBornScreeningResult"
                            id="newborn-screening-results"
                        />
                    </div>
                    <label for="birth-weight">
                        <strong class="py-1">Birth Weight</strong>
                    </label>
                    <div class="form-group row ps-0" id="birth-weight">
                        <div class="col-md-6">
                            <label for="birthWeightScale" class="mb-0"> Scale </label>
                            <EnumTypeaheadInput
                                :enum-list="weightScaleEnum"
                                v-model:prefill-value="patientHealthHistory.pediatricHistory.birthWeightScale"
                                placeholder="Select Scale"
                            />
                        </div>
                        <div class="col-md-6">
                            <label for="birth-weight-lb" class="mb-0">
                                Birth Weight {{ isWeightImperial ? "lb." : "kg." }}
                            </label>
                            <input
                                type="text"
                                class="form-control"
                                v-model="birthWeightLarge"
                                id="birth-weight-lb"
                                :placeholder="`${isWeightImperial ? 'lb.' : 'kg.'}`"
                                maxlength="5"
                                v-number-only
                            />
                            <span v-if="isWeightImperial">
                                <label for="birth-weight-oz" class="mb-0"> Birth Weight oz. </label>
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="birthWeightSmall"
                                    id="birth-weight-oz"
                                    placeholder="oz."
                                    maxlength="5"
                                    v-number-only
                                />
                            </span>
                        </div>
                    </div>
                    <div class="ps-0">
                        <strong class="py-1">Head Circumference</strong>
                        <div class="d-flex row align-items-end">
                            <div class="col-md-6">
                                <label for="headCircumferenceScale" class="mb-0 mt-1">
                                    <strong>Scale</strong>
                                </label>
                                <EnumTypeaheadInput
                                    :enum-list="lengthScaleEnum"
                                    v-model:prefill-value="patientHealthHistory.pediatricHistory.headCircumferenceScale"
                                    placeholder="Select Scale"
                                />
                            </div>
                            <div class="col-md-6">
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="patientHealthHistory.pediatricHistory.headCircumference"
                                    placeholder="${isCircumferenceImperial ? 'in.' : 'cm.'}"
                                    maxlength="5"
                                    number-only="decimal"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" src="./PediatricHistoryForm.ts" />
